import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Bookmark, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { pageContainer, pageItem } from '@/utils/animations';
import BookCard from '../components/BookCard';
import { useUserLibraryForTab } from '../hooks/useUserLibrary';
import type { LibraryItem } from '../api/library';

type BookStatus = 'in-progress' | 'saved' | 'completed';

const tabs: { id: BookStatus; label: string; icon: React.ElementType }[] = [
  { id: 'in-progress', label: 'در حال خواندن', icon: BookOpen },
  { id: 'saved', label: 'ذخیره شده', icon: Bookmark },
  { id: 'completed', label: 'خوانده شده', icon: CheckCircle },
];

const BookList = ({ items }: { items: LibraryItem[] }) => {
  if (!items.length) {
    return (
      <div className="text-center text-xs md:text-sm text-muted-foreground py-10">کتابی برای نمایش وجود ندارد.</div>
    );
  }
  return (
    <motion.div
      className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-x-6 gap-y-6"
      variants={pageContainer}
      initial="hidden"
      animate="visible"
    >
      {items.map((li) => (
        <BookCard
          key={li.book.book_id}
          book={li.book}
          progress={li.progress}
          isSaved={Boolean(li.is_saved)}
          showActions
        />
      ))}
    </motion.div>
  );
};

const SavedBooksPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<BookStatus>('in-progress');
  const { data, isLoading, isError } = useUserLibraryForTab(activeTab);
  const items = useMemo(() => data?.items ?? [], [data]);

  return (
    <motion.div
      className="min-h-screen bg-background pb-28"
      dir="rtl"
      variants={pageContainer}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <div className="bg-[#ffffff] px-4 py-3 flex items-center justify-between sticky top-0 z-50 mt-0.5">
        <button 
          onClick={() => navigate(-1)}
          className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="بازگشت"
        >
          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path>
          </svg>
        </button>
        <h1 className="text-sm md:text-base font-bold text-foreground">کتاب‌های من</h1>
        <div className="w-8"></div>
      </div>

      {/* Tabs */}
      <motion.div className="px-6 pt-4 sticky top-[52px] bg-background z-40" variants={pageItem}>
        <div className="flex space-x-4 space-x-reverse">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={isActive ? { borderBottomColor: '#7063ff' } : undefined}
                className={`flex-1 whitespace-nowrap py-3 px-1 border-b-2 font-medium text-xs md:text-sm transition-colors focus:outline-none flex items-center justify-center gap-2 ${
                  isActive
                    ? 'text-[#7063ff]'
                    : 'border-transparent text-muted-foreground hover:text-[#7063ff]/60'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </motion.div>

      {/* Content */}
      <main className="px-6 mt-8">
        {isLoading && (
          <div className="text-center text-xs md:text-sm text-muted-foreground py-10">در حال بارگذاری...</div>
        )}
        {isError && (
          <div className="text-center text-xs md:text-sm text-destructive py-10">خطا در دریافت اطلاعات کتابخانه.</div>
        )}
        {!isLoading && !isError && (
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <BookList items={items} />
            </motion.div>
          </AnimatePresence>
        )}
      </main>
    </motion.div>
  );
};

export default SavedBooksPage;
